import {
    AccountStatus,
    AnalyticsAuthErrorCode,
    AnalyticsAuthMethod,
    AnalyticsAuthStage,
    AnalyticsContentType,
    AnalyticsEvents,
    AnalyticsFormOutcome,
    AnalyticsPaymentType,
    AnalyticsTransactionStage,
    AnalyticsTransactionType,
    AnalyticsUserProperties,
    ApiException,
} from 'mh-client';

const events = AnalyticsEvents.getInstance();
const userProps = AnalyticsUserProperties.getInstance();

// GTM's dataLayer is cumulative, so omitted params bleed across events. Clear the
// previous push's keys with `undefined` (which GA4 drops) unless this event sets them.
let lastPushedKeys: string[] = [];

function push(event: string, params: object): void {
    if (typeof window === 'undefined' || !window.dataLayer) return;
    const reset: Record<string, undefined> = {};
    for (const key of lastPushedKeys) {
        if (!(key in params)) reset[key] = undefined;
    }
    window.dataLayer.push({ event, ...reset, ...params });
    lastPushedKeys = Object.keys(params);
}

function setUserProperty(name: string, value: string): void {
    if (typeof window === 'undefined' || !window.gtag) return;
    window.gtag('set', 'user_properties', { [name]: value });
}

interface PageViewParams {
    screen_name: string;
    page_title?: string;
}

export function pageView(params: PageViewParams): void {
    if (!params.screen_name) return;
    if (typeof document === 'undefined') return;
    push(events.PAGE_VIEW, {
        screen_name: params.screen_name,
        page_title: params.page_title ?? document.title,
    });
}

// ─── Generic interaction events ────────────────────────────────────

interface SwipeParams {
    screen_name: string;
    content_id: string;
    /** 1-based index of the slide now in view. */
    slide_index: number;
    slide_count: number;
}

/** Fires on settle when a pager lands on a new slide. */
export function swipe(params: SwipeParams): void {
    push(events.SWIPE, params);
}

interface CtaClickParams {
    screen_name: string;
    cta_id: string;
    destination?: string;
    /** 1-based slide position, for stepped flows (e.g. the transfer guide). */
    slide_index?: number;
}

export function ctaClick(params: CtaClickParams): void {
    push(events.CTA_CLICK, params);
}

interface ClickApplyParams {
    screen_name: string;
    click_id: string;
    value?: string;
    destination?: string;
}

export function clickApply(params: ClickApplyParams): void {
    push(events.CLICK_APPLY, params);
}

interface NavClickParams {
    nav_location: string;
    destination: string;
    screen_name?: string;
}

export function navClick(params: NavClickParams): void {
    push(events.NAV_CLICK, params);
}

interface LinkClickParams {
    screen_name: string;
    link_id: string;
    destination?: string;
}

export function linkClick(params: LinkClickParams): void {
    push(events.LINK_CLICK, params);
}

interface FilterApplyParams {
    screen_name: string;
    filter_type: string;
    filter_value: string;
}

export function filterApply(params: FilterApplyParams): void {
    push(events.FILTER_APPLY, params);
}

interface DropdownSelectParams {
    screen_name: string;
    dropdown_id: string;
    value: string;
}

export function dropdownSelect(params: DropdownSelectParams): void {
    push(events.DROPDOWN_SELECT, params);
}

interface ContentViewParams {
    screen_name: string;
    content_type: AnalyticsContentType;
    content_id: string;
}

export function contentView(params: ContentViewParams): void {
    push(events.CONTENT_VIEW, {
        screen_name: params.screen_name,
        content_type: params.content_type.value,
        content_id: params.content_id,
    });
}

/**
 * Fires a `content_view` for a tooltip hint, no-op when the screen name is
 * unresolved (e.g. a component mounted outside a data router). Collapses the
 * repeated screen-name guard + `content_type: Tooltip` boilerplate at call sites.
 */
export function tooltipView(
    screenName: string | undefined,
    contentId: string
): void {
    if (!screenName) return;
    contentView({
        screen_name: screenName,
        content_type: AnalyticsContentType.Tooltip,
        content_id: contentId,
    });
}

interface FormStartParams {
    form_id: string;
    screen_name: string;
}

export function formStart(params: FormStartParams): void {
    push(events.FORM_START, params);
}

interface FormSubmitParams {
    form_id: string;
    screen_name: string;
    outcome: AnalyticsFormOutcome;
}

export function formSubmit(params: FormSubmitParams): void {
    push(events.FORM_SUBMIT, {
        form_id: params.form_id,
        screen_name: params.screen_name,
        outcome: params.outcome.value,
    });
}

// ─── Auth events ───────────────────────────────────────────────────

interface LoginParams {
    method: AnalyticsAuthMethod;
    stage: AnalyticsAuthStage;
    error_code?: AnalyticsAuthErrorCode;
}

export function login(params: LoginParams): void {
    push(events.LOGIN, {
        method: params.method.value,
        stage: params.stage.value,
        ...(params.error_code && { error_code: params.error_code.value }),
    });
}

export function reauth(params: LoginParams): void {
    push(events.REAUTH, {
        method: params.method.value,
        stage: params.stage.value,
        ...(params.error_code && { error_code: params.error_code.value }),
    });
}

export function authErrorCode(
    error: unknown,
    invalid: AnalyticsAuthErrorCode
): AnalyticsAuthErrorCode {
    return error instanceof ApiException
        ? invalid
        : AnalyticsAuthErrorCode.Unknown;
}

interface SignUpParams {
    method: AnalyticsAuthMethod;
}

export function signUp(params: SignUpParams): void {
    push(events.SIGN_UP, { method: params.method.value });
}

// ─── Ecommerce + transaction events ─────────────────────────

export interface AnalyticsItem {
    item_id: string;
    item_name: string;
    item_category: string;
    price: number;
    quantity: number;
}

interface TransactionBaseParams {
    currency: string;
    value: number;
    items: AnalyticsItem[];
}

export function fundTransaction(
    ticker: string,
    sharePrice: number,
    shares: number
): TransactionBaseParams {
    return {
        currency: 'USD',
        value: sharePrice * shares,
        items: [
            {
                item_id: ticker.toLowerCase(),
                item_name: ticker.toUpperCase(),
                item_category: 'fund',
                price: sharePrice,
                quantity: shares,
            },
        ],
    };
}

// GTM reads ecommerce from a nested `ecommerce` object, not flat keys; clear
// the prior one first so item arrays don't merge across events.
function pushEcommerce(
    event: string,
    ecommerce: TransactionBaseParams,
    params: object = {}
): void {
    if (typeof window === 'undefined' || !window.dataLayer) return;
    window.dataLayer.push({ ecommerce: null });
    push(event, { ecommerce, ...params });
}

export function addToCart(params: TransactionBaseParams): void {
    pushEcommerce(events.ADD_TO_CART, params);
}

export function beginCheckout(params: TransactionBaseParams): void {
    pushEcommerce(events.BEGIN_CHECKOUT, params);
}

interface AddPaymentInfoParams extends TransactionBaseParams {
    payment_type: AnalyticsPaymentType;
}

export function addPaymentInfo(params: AddPaymentInfoParams): void {
    const { payment_type, ...ecommerce } = params;
    pushEcommerce(events.ADD_PAYMENT_INFO, ecommerce, {
        payment_type: payment_type.value,
    });
}

interface SellInitiatedParams extends TransactionBaseParams {
    fund_id?: string;
    share_price?: number;
    num_shares?: number;
}

export function sellInitiated(params: SellInitiatedParams): void {
    const { currency, value, items, ...rest } = params;
    pushEcommerce(events.SELL_INITIATED, { currency, value, items }, rest);
}

interface SellPendingParams extends TransactionBaseParams {
    transaction_id?: string;
}

export function sellPending(params: SellPendingParams): void {
    const { currency, value, items, ...rest } = params;
    pushEcommerce(events.SELL_PENDING, { currency, value, items }, rest);
}

interface TransactionCancelledParams {
    transaction_type: AnalyticsTransactionType;
    stage: AnalyticsTransactionStage;
}

export function transactionCancelled(params: TransactionCancelledParams): void {
    push(events.TRANSACTION_CANCELLED, {
        transaction_type: params.transaction_type.value,
        stage: params.stage.value,
    });
}

// ─── User properties ──────────────────────────────────────────────────────

export function setKycStatus(status: AccountStatus | null): void {
    setUserProperty(userProps.KYC_STATUS, status?.toAnalyticsValue() ?? 'none');
}

/** ISO 3166-1 alpha-2 country code (caller is responsible for mapping). */
export function setCountry(iso: string): void {
    setUserProperty(userProps.COUNTRY, iso);
}

export function setSignupCohort(cohort: string): void {
    setUserProperty(userProps.SIGNUP_COHORT, cohort);
}

export function setIsInternal(isInternal: boolean): void {
    setUserProperty(userProps.IS_INTERNAL, isInternal.toString());
}
